import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/lilia-ui/button";
import { ChoiceCards, KeyBar, SheetLabel } from "./InsertSheetParts";
import { TableDetail, TableList, tableOrder } from "./InsertSheetTables";
import { PaperDetail, PaperList, paperOrder } from "./InsertSheetPapers";
import { defaultSlot } from "./paperOutline";
import { touchRecentTable } from "./recentTables";
import {
  linkTableIntoDocument, placeViaServer, type InsertMode, type InsertResult, type PlaceBlock,
} from "./tableLinking";
import { tableName } from "./tableWords";
import { forgetPaper, useLinkPlaces, usePaper, useTableUsage } from "./usePaper";
import { useTableInsert, type DocSummary } from "./useTableInsert";
import type { TableSummary } from "./useTables";
import type { EditorLanding } from "./editorLanding";
import { tableDataToBlock } from "@/components/editor/table-grid/tableModel";
import { plain } from "@/components/editor/table-grid/tableCells";
import { testIds } from "@/testIds";

/**
 * Putting a table into a paper: the insert sheet.
 *
 * **Every choice is made while looking at the thing being chosen** (Olivia,
 * design-handoffs/2026-09-27-tables-modern). One sheet, two directions:
 *
 *   pickTable    (1a) from a paper: which of my tables goes in here?
 *   pickDocument (1b) from a table: which paper, and where in it?
 *
 * The left side is where you choose; the right side is what you would be
 * choosing: the table typeset as it will print, or the paper's outline with
 * the landing slot drawn in it. Link and Copy are cards with their
 * consequences, Link preselected, and the button names the outcome.
 *
 * What is listed, chosen and done lives in `useTableInsert` and `tableLinking`;
 * this file is the sheet and its keys.
 */

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/** 1a from the editor: where the table lands, and ⇥ to change it. */
export interface InsertLands {
  options: { id: EditorLanding; title: string; detail: string }[];
  value: EditorLanding;
  onChange: (l: EditorLanding) => void;
}

/** A blank table for "New table “…”": the query is its name. */
const blankTable = (caption: string) =>
  tableDataToBlock({
    caption, label: "", hasHeader: true, borders: "booktabs",
    align: ["l", "l", "l"],
    header: ["", "", ""].map(plain),
    rows: [0, 1, 2].map(() => ["", "", ""].map(plain)),
  });

const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

export function TableInsertDialog({
  mode,
  fetchWithAuth,
  tables,
  table,
  documentId,
  place,
  lands,
  onDone,
  onClose,
  onInserted,
  onGoTo,
  onNewBlankTable,
}: {
  mode: "pickTable" | "pickDocument";
  fetchWithAuth: Fetch;
  /** pickTable: the author's tables to choose from; undefined while loading. */
  tables?: TableSummary[];
  /** pickDocument: the table being placed. */
  table?: TableSummary;
  /** pickTable: the paper it goes into. */
  documentId?: string;
  /** How the block gets into the paper. Default: the server adds it. The
   *  editor passes its own, because the open canvas owns the document. */
  place?: PlaceBlock;
  /** pickTable, in the editor: the caret and the other places it can land. */
  lands?: InsertLands;
  /** The flow is finished: the table went in (the sheet closes). */
  onDone: (r: InsertResult) => void;
  onClose: () => void;
  /** pickDocument: a table went in and the sheet stays for the next paper. */
  onInserted?: (r: InsertResult) => void;
  /** pickTable: this paper already has the table; show it there. */
  onGoTo?: (blockId: string) => void;
  /** pickTable: New table instead, as an ordinary new table at the caret. */
  onNewBlankTable?: () => void;
}) {
  const navigate = useNavigate();
  const insert = useTableInsert({ mode, fetchWithAuth, tables, table, documentId, place, onDone });
  const { query, setQuery, rows, chosen, setChosen, busy, error, run, selected, matches, docs } = insert;

  const [choice, setChoice] = useState<InsertMode>("link");
  const [placing, setPlacing] = useState(false);
  const [picked, setPicked] = useState<{ paperId: string; index: number } | null>(null);
  const [usageVersion, setUsageVersion] = useState(0);
  const [toast, setToast] = useState<{ title: string; docId: string; blockId: string | null } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const pickTable = mode === "pickTable";

  // What is listed, in the order it is shown: Recent, then All.
  const tableLists = useMemo(
    () => (pickTable ? tableOrder(rows as TableSummary[], query) : { recent: [], rest: [] }),
    [pickTable, rows, query],
  );
  const paperLists = useMemo(
    () => (pickTable ? { recent: [], rest: [] } : paperOrder(rows as DocSummary[], query)),
    [pickTable, rows, query],
  );
  const ordered: { id: string }[] = pickTable
    ? [...tableLists.recent, ...tableLists.rest]
    : [...paperLists.recent, ...paperLists.rest];
  const orderKey = ordered.map((r) => r.id).join(",");

  // Something is always chosen, so the right side always shows it.
  // The functional update reads the current choice: a click that lands before
  // this effect runs (the list just appeared) must not be overwritten by the
  // first row, which a closure over the render's `chosen` (still null) did.
  useEffect(() => {
    setChosen((cur) => {
      if (!ordered.length) return null;
      return cur && ordered.some((r) => r.id === cur) ? cur : ordered[0].id;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey]);

  // Keep the chosen row in view as ↑↓ move it.
  useEffect(() => {
    if (!chosen) return;
    listRef.current?.querySelector(`[data-row-id="${chosen}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [chosen]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // The table in question, and the papers already using it.
  const theTable = pickTable ? (selected as TableSummary | undefined) : table;
  const usage = useTableUsage(fetchWithAuth, theTable?.id, usageVersion);
  const places = useLinkPlaces(fetchWithAuth, usage?.visible);
  const targetDoc = pickTable ? documentId : chosen ?? undefined;
  const existing = usage?.visible.find((l) => l.documentId === targetDoc) ?? null;
  const goTo = choice === "link" && !!existing;
  const existingPlace = existing ? places[existing.documentId] ?? null : null;

  // 1b: the paper, its outline, and where the table lands in it.
  const paper = usePaper(fetchWithAuth, pickTable ? null : chosen);
  // The author's pick for this paper, or the default: after the last section
  // that holds a table, otherwise the end.
  const slot = paper ? (picked?.paperId === paper.id ? picked.index : defaultSlot(paper.outline)) : null;
  const setSlot = (update: (s: number) => number) => {
    if (paper) setPicked({ paperId: paper.id, index: update(slot ?? 0) });
  };
  const landingSlot = paper && slot !== null ? paper.outline.slots[slot] : null;

  const papersAfter = (usage?.total ?? theTable?.documentCount ?? 0) + 1;

  const goThere = () => {
    if (!existing) return;
    if (pickTable) {
      if (existing.blockId) onGoTo?.(existing.blockId);
      onClose();
    } else {
      onClose();
      navigate(`/d/${existing.documentId}${existing.blockId ? `?block=${existing.blockId}` : ""}`);
    }
  };

  /** The primary action: ↵, and the filled button. */
  const commit = async () => {
    if (busy || !chosen) return;
    if (goTo) { goThere(); return; }
    if (pickTable) {
      const r = await run(choice);
      if (r && theTable) touchRecentTable(theTable.id);
      return;
    }
    // 1b: Insert and open — into the paper, then to it, scrolled to the table.
    const doc = chosen;
    const r = await run(choice, { place: placeViaServer(fetchWithAuth, landingSlot?.sortOrder), stay: true });
    if (!r) return;
    if (theTable) touchRecentTable(theTable.id);
    forgetPaper(doc);
    onDone(r);
    navigate(`/d/${doc}${r.blockId ? `?block=${r.blockId}` : ""}`);
  };

  /** 1b: Insert and stay — the sheet stays, ready for the next paper. */
  const commitStay = async () => {
    if (busy || !chosen || goTo) return;
    const doc = (rows as DocSummary[]).find((d) => d.id === chosen);
    const r = await run(choice, { place: placeViaServer(fetchWithAuth, landingSlot?.sortOrder), stay: true });
    if (!r) return;
    if (theTable) touchRecentTable(theTable.id);
    forgetPaper(chosen);
    setUsageVersion((v) => v + 1);
    setToast({ title: doc?.title || "Untitled", docId: chosen, blockId: r.blockId });
    onInserted?.(r);
  };

  /** New table “…”: a table of that name, made and linked in one go. */
  const newTable = async (name: string) => {
    if (!name) { onClose(); onNewBlankTable?.(); return; }
    if (!documentId) return;
    const res = await fetchWithAuth("/api/tables", {
      method: "POST",
      body: JSON.stringify({ caption: name, label: "", content: blankTable(name) }),
    });
    if (!res.ok) return;
    const made = (await res.json()) as TableSummary;
    const r = await linkTableIntoDocument(fetchWithAuth, { ...made, documentCount: 0 }, documentId, place);
    if (r) { touchRecentTable(made.id); onDone(r); }
  };

  const move = (delta: number) => {
    if (placing && paper) {
      const n = paper.outline.slots.length;
      setSlot((s) => Math.max(0, Math.min(n - 1, s + delta)));
      return;
    }
    if (!ordered.length) return;
    const i = ordered.findIndex((r) => r.id === chosen);
    const next = i < 0 ? 0 : i + delta;
    if (next < 0) { searchRef.current?.focus(); return; }
    setChosen(ordered[Math.min(ordered.length - 1, next)].id);
  };

  const cycleLanding = (back: boolean) => {
    if (pickTable) {
      if (!lands) return;
      const ids = lands.options.map((o) => o.id);
      const i = ids.indexOf(lands.value);
      lands.onChange(ids[(i + (back ? ids.length - 1 : 1)) % ids.length]);
      return;
    }
    setPlacing((p) => !p);
    listRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    const typing = isTyping(e.target);
    const mod = e.ctrlKey || e.metaKey || e.altKey;
    const handled = () => { e.preventDefault(); e.stopPropagation(); };

    if (e.key === "Escape") { handled(); onClose(); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      handled();
      if (typing) listRef.current?.focus();
      move(e.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (e.key === "Tab" && (typing || e.target === listRef.current)) {
      handled();
      cycleLanding(e.shiftKey);
      return;
    }
    if (e.key === "Enter" && !(e.target instanceof HTMLButtonElement)) {
      handled();
      void commit();
      return;
    }
    if (typing || mod) return;
    const k = e.key.toLowerCase();
    if (k === "l" || k === "c") { handled(); setChoice(k === "l" ? "link" : "copy"); return; }
    // Any other letter goes back to the search, which takes it.
    if (e.key.length === 1) searchRef.current?.focus();
  };

  // ── the footer ────────────────────────────────────────────────────────
  const n = <b>{papersAfter} paper{papersAfter === 1 ? "" : "s"}</b>;
  const linkWords: ReactNode = goTo
    ? <>Already in this paper{existingPlace?.where ? ` · ${existingPlace.where}` : ""}.</>
    : pickTable
      ? papersAfter <= 1 ? <>One table. Its edits reach this paper.</> : <>One table. Edits reach all {n} that use it.</>
      : papersAfter <= 1 ? <>One table. Its edits reach this paper.</> : <>Edits reach all {n}, including this one.</>;
  const copyWords: ReactNode = pickTable
    ? "This paper only, from now on. Remembers where it came from."
    : "A table of its own, in this paper only. Remembers where it came from.";

  const goLabel = existingPlace?.number ? `Go to Table ${existingPlace.number}` : "Go to the table";
  const commitLabel = busy ? "Adding…" : goTo ? goLabel
    : pickTable ? (choice === "link" ? "Insert linked table" : "Insert a copy")
      : "Insert and open";

  const current = lands?.options.find((o) => o.id === lands.value);

  return (
    <div
      className="lilia-scrim-in fixed inset-0 z-50 grid place-items-center p-6"
      style={{ background: "var(--lilia-scrim, rgba(15,22,32,.34))" }}
      onClick={onClose}
      data-testid={testIds.tableTool.insertDialog}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={pickTable ? "Insert a table" : `Use “${table ? tableName(table) : ""}” in a paper`}
        data-testid={testIds.tableTool.insertSheet}
        data-mode={mode}
        className="lilia-sheet-in flex w-full flex-col overflow-hidden"
        style={{
          maxWidth: 1180,
          height: "min(664px, calc(100vh - 48px))",
          background: "var(--lilia-surface)",
          border: "1px solid var(--lilia-border-strong)",
          borderRadius: 14,
          boxShadow: "0 24px 64px var(--lilia-shadow-lg)",
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex min-h-0 flex-1">
          {pickTable ? (
            <>
              <TableList
                recent={tableLists.recent}
                rest={tableLists.rest}
                total={tables?.length ?? 0}
                loading={tables === undefined}
                matches={matches}
                chosen={chosen}
                onChoose={setChosen}
                onCommit={() => void commit()}
                query={query}
                onQuery={setQuery}
                searchRef={searchRef}
                listRef={listRef}
                onNewTable={(name) => void newTable(name)}
              />
              <TableDetail table={theTable} match={chosen ? matches.get(chosen) : null} usage={usage} />
            </>
          ) : table ? (
            <>
              <PaperList
                table={table}
                recent={paperLists.recent}
                rest={paperLists.rest}
                loading={docs === null}
                chosen={chosen}
                onChoose={(id) => { setChosen(id); setPlacing(false); }}
                onCommit={() => void commit()}
                query={query}
                onQuery={setQuery}
                searchRef={searchRef}
                listRef={listRef}
                links={usage?.visible ?? []}
                places={places}
              />
              {chosen ? (
                <PaperDetail
                  paper={paper}
                  slot={slot ?? 0}
                  onSlot={(i) => { setSlot(() => i); setPlacing(true); }}
                  placing={placing}
                  already={existing ? existingPlace ?? { where: "", short: "", number: null } : null}
                  goTo={goTo}
                />
              ) : (
                <div className="flex-1" style={{ background: "var(--lilia-bg-secondary)" }} />
              )}
            </>
          ) : null}
        </div>

        {error && (
          <p className="shrink-0 border-t border-[var(--lilia-border)] px-[18px] py-1.5 text-[12px]" style={{ color: "var(--lilia-danger)" }}>
            {error}
          </p>
        )}

        <div className="flex shrink-0 items-stretch gap-3.5 border-t border-[var(--lilia-border)] px-[18px] py-3.5" style={{ background: "var(--lilia-surface)" }}>
          {pickTable && (
            <div
              data-testid={testIds.tableTool.insertLanding}
              data-landing={lands?.value ?? "end"}
              className="flex w-[300px] shrink-0 flex-col justify-center gap-1 border-r border-[var(--lilia-border)] pr-3.5"
            >
              <SheetLabel>Lands</SheetLabel>
              <div className="truncate text-[13px] text-[color:var(--lilia-text)]">
                <b>{current?.title ?? "At the end"}</b>
                {" · "}
                {current?.detail ?? "after the paper's last block"}
              </div>
              {lands && lands.options.length > 1 && (
                <button
                  type="button"
                  data-testid={testIds.tableTool.insertLandingChange}
                  onClick={() => cycleLanding(false)}
                  className="self-start text-[11.5px] font-semibold text-[color:var(--lilia-primary-ink)] underline-offset-2 hover:underline"
                >
                  Change place{" "}
                  <span className="text-[color:var(--lilia-text-secondary)]" style={{ fontFamily: "var(--lilia-font-mono)" }}>⇥</span>
                </button>
              )}
            </div>
          )}

          <ChoiceCards choice={choice} onChoice={setChoice} link={linkWords} copy={copyWords} keys={pickTable} />

          <div className="flex shrink-0 items-center gap-2.5">
            {!pickTable && !goTo && (
              <Button
                variant="tonal"
                data-testid={testIds.tableTool.insertCommitStay}
                disabled={!chosen || busy}
                onClick={() => void commitStay()}
              >
                Insert and stay
              </Button>
            )}
            <Button
              data-testid={testIds.tableTool.insertCommit}
              data-go-to={goTo || undefined}
              disabled={!chosen || busy}
              onClick={() => void commit()}
              className="min-w-[150px]"
            >
              {commitLabel}
            </Button>
          </div>
        </div>

        <KeyBar onClose={onClose}>
          {toast && (
            <span data-testid={testIds.tableTool.insertToast} className="truncate text-[11.5px] text-[color:var(--lilia-text)]">
              In “{toast.title}” ·{" "}
              <button
                type="button"
                data-testid={testIds.tableTool.insertToastOpen}
                onClick={() => { onClose(); navigate(`/d/${toast.docId}${toast.blockId ? `?block=${toast.blockId}` : ""}`); }}
                className="font-semibold text-[color:var(--lilia-primary-ink)] underline-offset-2 hover:underline"
              >
                Open
              </button>
            </span>
          )}
        </KeyBar>
      </div>
    </div>
  );
}
